/**
 * @module ClashDesMeutes
 * @description Team competition page — Rouge vs Bleu.
 * Giant progress bars, Top 3 warriors per team, join/switch, history.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Swords, Users, Crown, Medal, Shield, ShieldCheck, Flame, Zap,
  Trophy, Loader2, ChevronDown, ChevronUp, Star, Target,
  UtensilsCrossed, Droplets, Dumbbell, Scale, Link2,
  HelpCircle, X, Info, Share2, Download, Calendar, Sparkles
} from 'lucide-react';
import GamificationPanel from '../components/GamificationPanel';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import WeeklyMVP from '../components/WeeklyMVP';

const TEAM_CONFIG = {
  rouge: {
    gradient: 'from-red-600 to-red-400',
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    text: 'text-red-400',
    ring: 'ring-red-500/40',
    barBg: 'bg-red-500',
  },
  bleu: {
    gradient: 'from-blue-500 to-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    text: 'text-blue-400',
    ring: 'ring-blue-500/40',
    barBg: 'bg-blue-500',
  },
};

function TeamBar({ team, teamData, teamInfo, maxPoints, isUser, language }) {
  const fr = language === 'fr';
  const cfg = TEAM_CONFIG[team];
  const pct = maxPoints > 0 ? Math.min(100, Math.round((teamData.total_points / maxPoints) * 100)) : 0;

  return (
    <div className={`p-4 rounded-2xl ${cfg.bg} border ${cfg.border} ${isUser ? `ring-2 ${cfg.ring}` : ''}`} data-testid={`team-bar-${team}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{teamInfo.emoji}</span>
          <div>
            <p className={`font-heading text-lg uppercase tracking-tight ${cfg.text}`}>
              {fr ? teamInfo.name_fr : teamInfo.name_en}
            </p>
            <p className="text-[10px] text-muted-foreground">
              {teamData.member_count} {fr ? 'membre(s)' : 'member(s)'}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className={`font-heading text-3xl font-black ${cfg.text}`}>{teamData.total_points}</p>
          <p className="text-[9px] text-muted-foreground uppercase">pts</p>
        </div>
      </div>

      {/* Giant Progress Bar */}
      <div className="h-6 rounded-full bg-secondary/50 overflow-hidden mb-3">
        <motion.div
          className={`h-full rounded-full bg-gradient-to-r ${cfg.gradient}`}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
        />
      </div>

      {/* Top 3 Warriors */}
      {teamData.top_warriors && teamData.top_warriors.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            <Trophy className="w-3 h-3" /> Top 3 {fr ? 'Athletes' : 'Athletes'}
          </p>
          {teamData.top_warriors.map((w, i) => (
            <div key={w.user_id} className="flex items-center gap-2 py-1">
              <div className="w-5 flex justify-center">
                {i === 0 ? <Crown className="w-4 h-4 text-yellow-400" /> :
                 i === 1 ? <Medal className="w-4 h-4 text-gray-300" /> :
                 <Medal className="w-4 h-4 text-amber-600" />}
              </div>
              <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${cfg.gradient} flex items-center justify-center text-xs font-bold text-white`}>
                {(w.name || '?')[0].toUpperCase()}
              </div>
              <span className={`flex-1 text-sm truncate ${i === 0 ? 'font-bold text-foreground' : 'text-muted-foreground'}`}>
                {w.name}
              </span>
              <span className={`font-heading text-sm font-bold ${cfg.text}`}>{w.points}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* =============================================
   LE MUR DES INDESTRUCTIBLES — Streak Leaderboard
   ============================================= */
function MurDesIndestructibles({ language }) {
  const fr = language === 'fr';
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/leaderboard/streaks');
        setData(res.data);
      } catch { /* */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data || data.leaderboard.length === 0) {
    return (
      <div className="text-center py-16 space-y-3">
        <Link2 className="w-10 h-10 text-muted-foreground/40 mx-auto" />
        <p className="text-muted-foreground text-sm">
          {fr ? 'Aucun membre actif dans une equipe.' : 'No active team members.'}
        </p>
        <p className="text-xs text-muted-foreground/60">
          {fr ? 'Les membres doivent rejoindre une equipe pour apparaitre ici.' : 'Members must join a team to appear here.'}
        </p>
      </div>
    );
  }

  const { leaderboard, team_averages, user_streak, user_rank, leading_team } = data;

  // Podium: top 3
  const podium = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);

  return (
    <div className="space-y-6" data-testid="mur-des-indestructibles">
      {/* Header */}
      <div className="text-center space-y-1">
        <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight text-foreground">
          {fr ? 'Le Mur des Indestructibles' : 'The Wall of the Indestructible'}
        </h2>
        <p className="text-xs text-muted-foreground">
          {fr ? 'Semaines consecutives avec un Indice de Discipline >= 40' : 'Consecutive weeks with Discipline Index >= 40'}
        </p>
      </div>

      {/* Team Average Streaks — Rouge vs Bleu */}
      <Card className="bg-card/60 border-border overflow-hidden" data-testid="team-streak-averages">
        <CardContent className="p-0">
          <div className="grid grid-cols-2 divide-x divide-border">
            {/* Rouge */}
            <div className={`p-4 text-center ${leading_team === 'rouge' ? 'bg-red-500/10' : ''}`}>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{fr ? 'Equipe' : 'Team'} Rouge</p>
              <p className="font-heading text-4xl font-black text-red-400" data-testid="rouge-avg-streak">
                {team_averages.rouge}
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {fr ? 'streak moyen (sem.)' : 'avg streak (wks)'}
              </p>
              {leading_team === 'rouge' && (
                <div className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-[10px] font-bold">
                  <Crown className="w-3 h-3" /> {fr ? 'EN TETE' : 'LEADING'}
                </div>
              )}
            </div>
            {/* Bleu */}
            <div className={`p-4 text-center ${leading_team === 'bleu' ? 'bg-blue-500/10' : ''}`}>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{fr ? 'Equipe' : 'Team'} Bleu</p>
              <p className="font-heading text-4xl font-black text-blue-400" data-testid="bleu-avg-streak">
                {team_averages.bleu}
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {fr ? 'streak moyen (sem.)' : 'avg streak (wks)'}
              </p>
              {leading_team === 'bleu' && (
                <div className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 text-[10px] font-bold">
                  <Crown className="w-3 h-3" /> {fr ? 'EN TETE' : 'LEADING'}
                </div>
              )}
            </div>
          </div>
          {leading_team === 'tie' && (
            <div className="text-center py-2 bg-secondary/30 text-[10px] text-muted-foreground font-bold uppercase">
              {fr ? 'Egalite parfaite' : 'Perfect tie'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Accountability Message */}
      <div className="text-center px-4">
        <p className="text-[11px] text-amber-400/80 italic">
          {fr
            ? '"Si tu brises ta chaine, tu penalises la moyenne de ton escouade."'
            : '"If you break your chain, you penalize your squad\'s average."'
          }
        </p>
      </div>

      {/* Podium — Top 3 */}
      {podium.length > 0 && (
        <div className="flex items-end justify-center gap-3 pt-4 pb-2" data-testid="streak-podium">
          {/* 2nd Place */}
          {podium.length > 1 && (
            <PodiumCard entry={podium[1]} position={2} language={language} isCurrentUser={podium[1].user_id === user?.user_id} />
          )}
          {/* 1st Place */}
          <PodiumCard entry={podium[0]} position={1} language={language} isCurrentUser={podium[0].user_id === user?.user_id} />
          {/* 3rd Place */}
          {podium.length > 2 && (
            <PodiumCard entry={podium[2]} position={3} language={language} isCurrentUser={podium[2].user_id === user?.user_id} />
          )}
        </div>
      )}

      {/* Rest of Leaderboard */}
      {rest.length > 0 && (
        <Card className="bg-card/40 border-border" data-testid="streak-leaderboard-rest">
          <CardContent className="p-3 space-y-1">
            {rest.map((entry) => {
              const cfg = TEAM_CONFIG[entry.team_id] || TEAM_CONFIG.rouge;
              const isMe = entry.user_id === user?.user_id;
              return (
                <div
                  key={entry.user_id}
                  className={`flex items-center gap-3 py-2 px-3 rounded-lg ${isMe ? 'bg-primary/10 ring-1 ring-primary/30' : 'hover:bg-secondary/40'} transition-colors`}
                  data-testid={`streak-entry-${entry.rank}`}
                >
                  <span className="text-sm font-heading text-muted-foreground w-6 text-center">#{entry.rank}</span>
                  <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${cfg.gradient} flex items-center justify-center text-xs font-bold text-white flex-shrink-0`}>
                    {(entry.name || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate ${isMe ? 'font-bold text-foreground' : 'text-muted-foreground'}`}>
                      {entry.name} {isMe ? (fr ? '(Toi)' : '(You)') : ''}
                    </p>
                    <p className={`text-[10px] ${cfg.text}`}>
                      {fr ? 'Equipe' : 'Team'} {entry.team_id === 'rouge' ? 'Rouge' : 'Bleu'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Link2 className={`w-4 h-4 ${entry.streak > 0 ? 'text-amber-400' : 'text-muted-foreground/30'}`} />
                    <span className={`font-heading text-lg font-black ${entry.streak > 0 ? 'text-foreground' : 'text-muted-foreground/40'}`}>
                      {entry.streak}
                    </span>
                    <span className="text-[9px] text-muted-foreground">{fr ? 'sem.' : 'wks'}</span>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Your Position */}
      {user_rank > 0 && (
        <div className="text-center py-3">
          <p className="text-xs text-muted-foreground">
            {fr ? 'Ta position:' : 'Your position:'}{' '}
            <span className="font-heading font-bold text-foreground">#{user_rank}</span>
            {' '}{fr ? 'sur' : 'of'}{' '}{data.total_members}
            {user_streak > 0 && (
              <span className="ml-2 text-amber-400 font-bold">
                {user_streak} {fr ? 'semaine(s) consecutives' : 'consecutive week(s)'}
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
}

/* Podium Card for Top 3 */
function PodiumCard({ entry, position, language, isCurrentUser }) {
  const fr = language === 'fr';
  const cfg = TEAM_CONFIG[entry.team_id] || TEAM_CONFIG.rouge;

  const heights = { 1: 'h-28', 2: 'h-20', 3: 'h-16' };
  const sizes = { 1: 'w-14 h-14', 2: 'w-11 h-11', 3: 'w-11 h-11' };
  const textSizes = { 1: 'text-2xl', 2: 'text-lg', 3: 'text-lg' };
  const medalIcons = {
    1: <Crown className="w-5 h-5 text-yellow-400" />,
    2: <Medal className="w-4 h-4 text-gray-300" />,
    3: <Medal className="w-4 h-4 text-amber-600" />,
  };
  const glows = {
    1: 'ring-2 ring-yellow-400/50 shadow-lg shadow-yellow-400/20',
    2: 'ring-1 ring-gray-400/30',
    3: 'ring-1 ring-amber-600/30',
  };

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: position * 0.15 }}
      className={`flex flex-col items-center ${position === 1 ? 'order-2' : position === 2 ? 'order-1' : 'order-3'}`}
      data-testid={`podium-${position}`}
    >
      {/* Avatar */}
      <div className={`${sizes[position]} rounded-full bg-gradient-to-br ${cfg.gradient} flex items-center justify-center font-bold text-white ${glows[position]} ${isCurrentUser ? 'ring-primary ring-2' : ''}`}>
        {position === 1 ? (
          <span className="text-lg">{(entry.name || '?')[0].toUpperCase()}</span>
        ) : (
          <span className="text-sm">{(entry.name || '?')[0].toUpperCase()}</span>
        )}
      </div>
      {/* Medal */}
      <div className="my-1">{medalIcons[position]}</div>
      {/* Name */}
      <p className={`text-xs font-bold truncate max-w-[80px] text-center ${isCurrentUser ? 'text-primary' : 'text-foreground'}`}>
        {entry.name?.split(' ')[0] || '?'}
      </p>
      {/* Pedestal */}
      <div className={`${heights[position]} w-20 mt-1 rounded-t-lg bg-gradient-to-t ${cfg.gradient} flex flex-col items-center justify-center`}>
        <span className={`font-heading ${textSizes[position]} font-black text-white`}>{entry.streak}</span>
        <span className="text-[8px] text-white/70 uppercase">{fr ? 'sem.' : 'wks'}</span>
      </div>
    </motion.div>
  );
}


/* =============================================
   MONTHLY MVP — Classement mensuel + partage social
   ============================================= */
function MonthlyMVPSection({ language, currentUser }) {
  const fr = language === 'fr';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);
  const shareRef = useRef(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/clash/monthly-mvp');
        setData(res.data);
      } catch (e) {
        console.error('Monthly MVP fetch error:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleShare = useCallback(async () => {
    if (!shareRef.current) return;
    setGenerating(true);
    try {
      const { toPng } = await import('html-to-image');
      const dataUrl = await toPng(shareRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: '#0a0a0f' });
      if (navigator.share && navigator.canShare) {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], 'yumix-mvp-du-mois.png', { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ title: 'YUMIX - MVP du Mois', files: [file] });
          setGenerating(false);
          return;
        }
      }
      const link = document.createElement('a');
      link.download = 'yumix-mvp-du-mois.png';
      link.href = dataUrl;
      link.click();
    } catch (err) { console.error('Share error:', err); }
    finally { setGenerating(false); }
  }, []);

  if (loading) return (
    <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
  );
  if (!data) return null;

  const standings = data.current_standings || [];
  const lastMvp = data.last_month_mvp;
  const mvpHistory = data.mvp_history || [];
  const userMvpCount = data.user_mvp_count || 0;
  const currentMonth = data.current_month;

  const monthNames = {
    '01': 'Janvier', '02': 'Fevrier', '03': 'Mars', '04': 'Avril',
    '05': 'Mai', '06': 'Juin', '07': 'Juillet', '08': 'Aout',
    '09': 'Septembre', '10': 'Octobre', '11': 'Novembre', '12': 'Decembre'
  };
  const currentMonthLabel = currentMonth ? `${monthNames[currentMonth.split('-')[1]] || ''} ${currentMonth.split('-')[0]}` : '';

  const medals = ['🥇', '🥈', '🥉'];

  const isUserMvp = lastMvp && lastMvp.user_id === currentUser?.user_id;

  return (
    <div className="space-y-5" data-testid="monthly-mvp-section">
      {/* Header */}
      <div className="text-center space-y-1">
        <Crown className="w-8 h-8 text-yellow-400 mx-auto" />
        <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight text-foreground">
          {fr ? 'MVP du Mois' : 'Monthly MVP'}
        </h2>
        <p className="text-xs text-muted-foreground">
          {fr ? 'Le membre avec le plus de points Clash sur le mois entier' : 'Member with the most Clash points for the entire month'}
        </p>
      </div>

      {/* Last Month's MVP */}
      {lastMvp && (
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <Card className="bg-gradient-to-br from-yellow-500/10 to-amber-500/5 border-yellow-500/30 ring-1 ring-yellow-500/20 overflow-hidden" data-testid="last-month-mvp">
            <CardContent className="p-5">
              <div className="text-center space-y-3">
                <p className="text-xs uppercase tracking-widest text-yellow-400/70 font-heading">
                  {fr ? `MVP de ${lastMvp.month_label_fr || lastMvp.month}` : `MVP of ${lastMvp.month_label_fr || lastMvp.month}`}
                </p>
                <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-lg shadow-yellow-500/20">
                  <span className="text-3xl">👑</span>
                </div>
                <div>
                  <p className="font-heading text-xl text-foreground uppercase">{lastMvp.name}</p>
                  <p className="text-2xl font-black text-yellow-400">{lastMvp.points} <span className="text-sm font-normal">pts</span></p>
                  {lastMvp.mvp_count > 1 && (
                    <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-400 text-xs font-bold">
                      <Star className="w-3 h-3" /> MVP x{lastMvp.mvp_count}
                    </span>
                  )}
                </div>

                {/* Top 3 Podium */}
                {lastMvp.top3 && lastMvp.top3.length > 0 && (
                  <div className="space-y-1.5 pt-3 border-t border-yellow-500/10">
                    {lastMvp.top3.map((p, i) => (
                      <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${i === 0 ? 'bg-yellow-500/10' : 'bg-secondary/30'}`}>
                        <span className="text-lg">{medals[i]}</span>
                        <span className="flex-1 text-sm text-foreground truncate">{p.name?.split(' ')[0]}</span>
                        <span className={`font-heading font-bold text-sm ${i === 0 ? 'text-yellow-400' : 'text-muted-foreground'}`}>{p.points} pts</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Share button for MVP winner */}
                {isUserMvp && (
                  <Button
                    onClick={() => setShowShare(true)}
                    className="mt-3 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-heading uppercase hover:opacity-90"
                    data-testid="mvp-share-btn"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    {fr ? 'Partager mon titre' : 'Share my title'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Current Month Live Standings */}
      <Card className="bg-card/80 border-border" data-testid="monthly-standings">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-heading uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {fr ? `Classement en cours — ${currentMonthLabel}` : `Current standings — ${currentMonthLabel}`}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {standings.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              {fr ? 'Aucun point ce mois-ci. Logue un repas pour commencer!' : 'No points this month. Log a meal to start!'}
            </p>
          ) : (
            <div className="space-y-1.5">
              {standings.map((s, i) => {
                const isMe = s.user_id === currentUser?.user_id;
                const teamCfg = TEAM_CONFIG[s.team_id] || TEAM_CONFIG.rouge;
                return (
                  <div
                    key={s.user_id}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${isMe ? 'bg-primary/10 ring-1 ring-primary/30' : 'bg-secondary/30'}`}
                    data-testid={`standing-${i}`}
                  >
                    <span className={`w-6 text-center font-heading font-bold text-sm ${i < 3 ? 'text-yellow-400' : 'text-muted-foreground'}`}>
                      {i < 3 ? medals[i] : `#${i + 1}`}
                    </span>
                    <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${teamCfg.gradient} flex items-center justify-center text-white text-xs font-bold`}>
                      {(s.name || '?')[0].toUpperCase()}
                    </div>
                    <span className={`flex-1 text-sm truncate ${isMe ? 'text-primary font-bold' : 'text-foreground'}`}>
                      {s.name?.split(' ')[0]}
                      {s.mvp_count > 0 && <span className="ml-1 text-yellow-400 text-[10px]">👑 x{s.mvp_count}</span>}
                    </span>
                    <span className={`font-heading font-bold text-sm ${teamCfg.text}`}>{s.points}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* MVP History */}
      {mvpHistory.length > 0 && (
        <Card className="bg-card/80 border-border" data-testid="mvp-history">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-xs font-heading uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Trophy className="w-3.5 h-3.5" />
              {fr ? 'Palmares des MVP' : 'MVP Hall of Fame'}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="space-y-1.5">
              {mvpHistory.map((m, i) => (
                <div key={m.month} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-secondary/20 text-xs">
                  <span className="text-yellow-400">👑</span>
                  <span className="text-muted-foreground w-20">{m.month_label_fr || m.month}</span>
                  <span className="flex-1 text-foreground font-medium truncate">{m.name?.split(' ')[0]}</span>
                  <span className="text-yellow-400 font-bold">{m.points} pts</span>
                  {m.mvp_count > 1 && <span className="text-[10px] text-yellow-400/60">x{m.mvp_count}</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info about monthly reset */}
      <div className="text-center p-3 rounded-xl bg-primary/5 border border-primary/15">
        <p className="text-[11px] text-primary/80">
          {fr
            ? 'Les equipes sont reassignees automatiquement au debut de chaque mois pour garder l\'equilibre.'
            : 'Teams are auto-reassigned at the start of each month to maintain balance.'}
        </p>
      </div>

      {/* Share Modal for MVP winner */}
      {showShare && lastMvp && (
        <MVPShareModal
          show={showShare}
          onClose={() => setShowShare(false)}
          mvpData={lastMvp}
          shareRef={shareRef}
          handleShare={handleShare}
          generating={generating}
          language={language}
        />
      )}
    </div>
  );
}


/* =============================================
   MVP SHARE MODAL — Social image
   ============================================= */
function MVPShareModal({ show, onClose, mvpData, shareRef, handleShare, generating, language }) {
  const fr = language === 'fr';
  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        onClick={onClose}
        data-testid="mvp-share-modal-overlay"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="max-w-sm w-full"
          onClick={e => e.stopPropagation()}
        >
          {/* Shareable Image Card */}
          <div
            ref={shareRef}
            className="rounded-2xl overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #0a0a0f, #1a1a2e)' }}
            data-testid="mvp-share-card"
          >
            <div className="p-6 text-center space-y-4">
              {/* Logo */}
              <div>
                <span className="text-primary font-heading text-lg tracking-widest">YUMIX</span>
                <p className="text-[10px] text-muted-foreground uppercase tracking-widest">Extra Miles</p>
              </div>

              {/* Crown */}
              <div className="flex justify-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center shadow-xl shadow-yellow-500/30">
                  <span className="text-4xl">👑</span>
                </div>
              </div>

              {/* Title */}
              <div>
                <p className="text-xs uppercase tracking-widest text-yellow-400/60">
                  {mvpData.mvp_count > 1 ? `MVP x${mvpData.mvp_count}` : 'MVP DU MOIS'}
                </p>
                <h3 className="font-heading text-2xl text-white uppercase mt-1">{mvpData.name}</h3>
                <p className="text-yellow-400/80 text-sm mt-1">{mvpData.month_label_fr || mvpData.month}</p>
              </div>

              {/* Points */}
              <div className="bg-yellow-500/10 rounded-xl py-3 px-4 inline-block">
                <span className="font-heading text-4xl font-black text-yellow-400">{mvpData.points}</span>
                <span className="text-sm text-yellow-400/60 ml-1">pts</span>
              </div>

              {/* Top 3 */}
              {mvpData.top3 && (
                <div className="space-y-1 text-xs">
                  {mvpData.top3.map((p, i) => (
                    <div key={i} className="flex items-center justify-center gap-2">
                      <span>{['🥇', '🥈', '🥉'][i]}</span>
                      <span className="text-white/80">{p.name?.split(' ')[0]}</span>
                      <span className="text-yellow-400/60">{p.points} pts</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Tagline */}
              <p className="text-primary text-xs font-heading tracking-widest">ALL IN ALL TIME !!!</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-3">
            <Button
              onClick={handleShare}
              disabled={generating}
              className="flex-1 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-heading uppercase hover:opacity-90"
              data-testid="mvp-share-download"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
              {fr ? 'Partager' : 'Share'}
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              className="px-4"
              data-testid="mvp-share-close"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}


/* =============================================
   RULES MODAL — Scoring Rules Display
   ============================================= */
function RulesModal({ isOpen, onClose, tab, language }) {
  const fr = language === 'fr';
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="rules-modal-overlay"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-card border border-border rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto shadow-2xl"
          onClick={e => e.stopPropagation()}
          data-testid="rules-modal"
        >
          {/* Header */}
          <div className="sticky top-0 bg-card/95 backdrop-blur-md border-b border-border p-4 flex items-center justify-between rounded-t-2xl z-10">
            <h2 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2">
              <Info className="w-5 h-5 text-primary" />
              {fr ? 'Règles du Jeu' : 'Game Rules'}
            </h2>
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-secondary/60 transition-colors" data-testid="rules-modal-close">
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          <div className="p-4 space-y-5">
            {tab === 'clash' ? (
              <>
                {/* CLASH RULES */}
                <div className="text-center space-y-1">
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-2xl">🔴</span>
                    <Swords className="w-6 h-6 text-primary" />
                    <span className="text-2xl">🔵</span>
                  </div>
                  <h3 className="font-heading text-xl uppercase text-foreground">
                    {fr ? 'Clash des Meutes' : 'Clash of Packs'}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {fr ? 'Competition hebdomadaire par equipe (lundi au dimanche)' : 'Weekly team competition (Monday to Sunday)'}
                  </p>
                </div>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  {fr
                    ? 'Les points sont calcules chaque jour. Toutes les actions de tous les membres sont additionnees pour former le score de l\'equipe.'
                    : 'Points are calculated daily. All actions from all members are added to form the team score.'}
                </p>

                {/* Points Table */}
                <div className="space-y-2">
                  <p className="text-xs font-heading uppercase text-primary tracking-wider">
                    {fr ? 'Bareme des Points' : 'Point Values'}
                  </p>

                  {[
                    { icon: <UtensilsCrossed className="w-4 h-4" />, action: fr ? 'Repas logue' : 'Meal logged', pts: 10, detail: fr ? 'Par repas (illimite)' : 'Per meal (unlimited)', color: 'text-orange-400', bg: 'bg-orange-500/10' },
                    { icon: <Droplets className="w-4 h-4" />, action: fr ? 'Objectif eau atteint' : 'Water goal met', pts: 15, detail: fr ? '1x par jour' : '1x per day', color: 'text-blue-400', bg: 'bg-blue-500/10' },
                    { icon: <Dumbbell className="w-4 h-4" />, action: fr ? 'Workout logue' : 'Workout logged', pts: 20, detail: fr ? 'Par entraînement' : 'Per workout', color: 'text-green-400', bg: 'bg-green-500/10' },
                    { icon: <Scale className="w-4 h-4" />, action: fr ? 'Poids logue' : 'Weight logged', pts: 5, detail: fr ? '1x par jour' : '1x per day', color: 'text-purple-400', bg: 'bg-purple-500/10' },
                  ].map((row, i) => (
                    <div key={i} className={`flex items-center gap-3 p-3 rounded-xl ${row.bg}`}>
                      <div className={`${row.color}`}>{row.icon}</div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{row.action}</p>
                        <p className="text-[10px] text-muted-foreground">{row.detail}</p>
                      </div>
                      <span className={`font-heading text-lg font-black ${row.color}`}>+{row.pts}</span>
                    </div>
                  ))}
                </div>

                {/* Example */}
                <div className="p-3 rounded-xl bg-primary/5 border border-primary/20">
                  <p className="text-xs text-primary font-medium">
                    {fr ? 'Exemple: 3 repas + 1 workout + eau = 30 + 20 + 15 = 65 pts/jour' : 'Example: 3 meals + 1 workout + water = 30 + 20 + 15 = 65 pts/day'}
                  </p>
                </div>

                {/* ⚡ BONUS TACTIQUES — Nouvelles mécaniques de gamification */}
                <div className="space-y-2 pt-2">
                  <p className="text-xs font-heading uppercase text-primary tracking-wider flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5 text-yellow-400" />
                    {fr ? 'Bonus Tactiques' : 'Tactical Bonuses'}
                  </p>

                  {/* Card 1 — Multiplicateur "Extra Mile" */}
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20" data-testid="bonus-card-perfect-day">
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-yellow-300 leading-tight">
                        {fr ? 'Journée Parfaite' : 'Perfect Day'}
                      </p>
                      <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                        {fr
                          ? "Logue ton objectif d'eau, tes repas et 1 entraînement dans la même journée pour activer un multiplicateur (×1.2) sur tous tes points du lendemain !"
                          : 'Log your water goal, meals and 1 workout on the same day to activate a multiplier (×1.2) on all your points the next day!'}
                      </p>
                    </div>
                  </div>

                  {/* Card 2 — Bonus de Peloton */}
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20" data-testid="bonus-card-team-force">
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                      <ShieldCheck className="w-5 h-5 text-indigo-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-indigo-300 leading-tight">
                        {fr ? "Force de Frappe d'Équipe" : 'Team Strike Force'}
                      </p>
                      <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                        {fr
                          ? "Ne laisse personne derrière. Si 80% de ton équipe complète un entraînement le même jour, l'équipe entière reçoit un bonus de +200 pts."
                          : 'Leave no one behind. If 80% of your team complètes a workout on the same day, the entire team receives a +200 pts bonus.'}
                      </p>
                    </div>
                  </div>

                  {/* Card 3 — Mission Tactique */}
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-orange-500/10 border border-orange-500/20" data-testid="bonus-card-weekly-boost">
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-orange-500/20 flex items-center justify-center">
                      <Target className="w-5 h-5 text-orange-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-orange-300 leading-tight">
                        {fr ? 'Objectif de la Semaine' : 'Weekly Objective'}
                      </p>
                      <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                        {fr
                          ? "Chaque lundi, le QG lance une cible prioritaire (ex: l'hydratation donne le double des points). Surveillez vos alertes."
                          : 'Every Monday, HQ launches a priority target (e.g., hydration gives double points). Watch your alerts.'}
                      </p>
                    </div>
                  </div>
                </div>

                <p className="text-[11px] text-muted-foreground text-center italic">
                  {fr ? 'L\'equipe est assignee automatiquement pour garder l\'equilibre.' : 'Teams are auto-assigned to maintain balance.'}
                </p>
              </>
            ) : (
              <>
                {/* INDESTRUCTIBLES RULES */}
                <div className="text-center space-y-1">
                  <Link2 className="w-8 h-8 text-amber-400 mx-auto" />
                  <h3 className="font-heading text-xl uppercase text-foreground">
                    {fr ? 'Mur des Indestructibles' : 'Wall of the Indestructible'}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {fr ? 'Semaines consecutives de discipline' : 'Consecutive disciplined weeks'}
                  </p>
                </div>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  {fr
                    ? 'Chaque semaine, un "Indice de Discipline" est calcule sur 100 points. Si ton score est >= 40, ta semaine compte comme "disciplinee" et ton streak continue.'
                    : 'Each week, a "Discipline Index" is calculated out of 100. If your score is >= 40, the week counts as "disciplined" and your streak continues.'}
                </p>

                {/* Discipline Index Breakdown */}
                <div className="space-y-2">
                  <p className="text-xs font-heading uppercase text-amber-400 tracking-wider">
                    {fr ? 'Indice de Discipline (sur 100)' : 'Discipline Index (out of 100)'}
                  </p>

                  {[
                    { icon: <UtensilsCrossed className="w-4 h-4" />, name: fr ? 'Repas logues' : 'Meals logged', max: 35, target: fr ? '21 repas/sem = max (3/jour x 7)' : '21 meals/week = max', color: 'text-orange-400', bg: 'bg-orange-500/10' },
                    { icon: <Dumbbell className="w-4 h-4" />, name: fr ? 'Workouts' : 'Workouts', max: 30, target: fr ? '4 workouts/sem = max' : '4 workouts/week = max', color: 'text-green-400', bg: 'bg-green-500/10' },
                    { icon: <Droplets className="w-4 h-4" />, name: fr ? 'Jours d\'hydratation' : 'Hydration days', max: 20, target: fr ? '5 jours/sem avec eau loguee = max' : '5 days/week with water logged = max', color: 'text-blue-400', bg: 'bg-blue-500/10' },
                    { icon: <Flame className="w-4 h-4" />, name: fr ? 'Bonus Constance' : 'Consistency Bonus', max: 15, target: fr ? '>= 14 repas ET >= 3 workouts ET >= 4 jours eau' : '>= 14 meals AND >= 3 workouts AND >= 4 water days', color: 'text-amber-400', bg: 'bg-amber-500/10' },
                  ].map((row, i) => (
                    <div key={i} className={`flex items-center gap-3 p-3 rounded-xl ${row.bg}`}>
                      <div className={`${row.color}`}>{row.icon}</div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{row.name}</p>
                        <p className="text-[10px] text-muted-foreground">{row.target}</p>
                      </div>
                      <span className={`font-heading text-base font-black ${row.color}`}>{row.max}</span>
                    </div>
                  ))}
                </div>

                {/* Streak Rule */}
                <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/20 space-y-1.5">
                  <p className="text-xs text-amber-400 font-medium">
                    {fr ? 'Seuil minimum: 40 pts = semaine disciplinee' : 'Minimum threshold: 40 pts = disciplined week'}
                  </p>
                  <p className="text-xs text-amber-400/70">
                    {fr ? 'Streak = nombre de semaines consecutives a 40+' : 'Streak = consecutive weeks at 40+'}
                  </p>
                </div>

                {/* Team Impact */}
                <div className="p-3 rounded-xl bg-red-500/5 border border-red-500/20">
                  <p className="text-[11px] text-red-400 italic text-center font-medium">
                    {fr
                      ? '"Si tu brises ta chaine, tu penalises la moyenne de ton equipe."'
                      : '"If you break your chain, you penalize your team\'s average."'}
                  </p>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}


export default function ClashDesMeutes() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const fr = language === 'fr';

  const [activeTab, setActiveTab] = useState('clash');
  const [showRules, setShowRules] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/clash/status');
      setData(res.data);
    } catch { /* */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleLogPoints = async () => {
    setScoring(true);
    try {
      const res = await api.post('/clash/log-points');
      if (res.data.success) {
        toast.success(fr
          ? `+${res.data.points} pts pour ton equipe!`
          : `+${res.data.points} pts for your team!`);
        await fetchStatus();
      } else {
        toast.info(fr ? 'Rejoins une equipe d\'abord!' : 'Join a team first!');
      }
    } catch { /* */ }
    finally { setScoring(false); }
  };

  const loadHistory = async () => {
    if (history.length > 0) { setShowHistory(!showHistory); return; }
    try {
      const res = await api.get('/clash/history');
      setHistory(res.data.history || []);
      setShowHistory(true);
    } catch { /* */ }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-8 text-center">
          <p className="text-muted-foreground">{fr ? 'Erreur de chargement' : 'Loading error'}</p>
        </div>
      </Layout>
    );
  }

  const maxPoints = Math.max(data.rouge.total_points, data.bleu.total_points, 100);
  const userTeam = data.user_team;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6" data-testid="clash-page">
        {/* Tab Selector */}
        <div className="flex items-center gap-2" data-testid="clash-tabs">
          <div className="flex flex-1 bg-secondary/40 rounded-xl p-1 gap-1">
            <button
              onClick={() => setActiveTab('clash')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-heading uppercase tracking-wide transition-all ${
                activeTab === 'clash'
                  ? 'bg-card text-foreground shadow-md'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-clash"
            >
              <Swords className="w-4 h-4" />
              {fr ? 'Clash' : 'Clash'}
            </button>
            <button
              onClick={() => setActiveTab('mur')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-heading uppercase tracking-wide transition-all ${
                activeTab === 'mur'
                  ? 'bg-card text-foreground shadow-md'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-mur"
            >
              <Link2 className="w-4 h-4" />
              {fr ? 'Indestructibles' : 'Indestructibles'}
            </button>
            <button
              onClick={() => setActiveTab('mvp')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-heading uppercase tracking-wide transition-all ${
                activeTab === 'mvp'
                  ? 'bg-card text-foreground shadow-md'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-mvp"
            >
              <Crown className="w-4 h-4" />
              MVP
            </button>
          </div>
          <button
            onClick={() => setShowRules(true)}
            className="p-2.5 rounded-xl bg-secondary/40 hover:bg-secondary/70 transition-colors"
            title={fr ? 'Règles du jeu' : 'Game rules'}
            data-testid="rules-button"
          >
            <HelpCircle className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Rules Modal */}
        <RulesModal isOpen={showRules} onClose={() => setShowRules(false)} tab={activeTab} language={language} />

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'mvp' ? (
            <motion.div key="mvp" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
              <MonthlyMVPSection language={language} currentUser={user} />
            </motion.div>
          ) : activeTab === 'mur' ? (
            <motion.div key="mur" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
              <MurDesIndestructibles language={language} />
            </motion.div>
          ) : (
            <motion.div key="clash" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.2 }}>
              <div className="space-y-6">
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <span className="text-3xl">🔴</span>
            <Swords className="w-8 h-8 text-primary" />
            <span className="text-3xl">🔵</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight text-foreground">
            {fr ? 'Clash des Meutes' : 'Clash of Packs'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {fr ? `Semaine: ${data.week_start} → ${data.week_end}` : `Week: ${data.week_start} → ${data.week_end}`}
          </p>
          {data.leading && (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className={`inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full text-xs font-bold ${TEAM_CONFIG[data.leading].bg} ${TEAM_CONFIG[data.leading].text} ${TEAM_CONFIG[data.leading].border} border`}
            >
              <Crown className="w-3.5 h-3.5" />
              {data.teams[data.leading]?.emoji} {fr ? data.teams[data.leading]?.name_fr : data.teams[data.leading]?.name_en} {fr ? 'en tete!' : 'leading!'}
            </motion.div>
          )}
        </div>

        {/* Auto-assignment notice (if no team yet — clients only) */}
        {!userTeam && user?.role === 'client' && (
          <Card className="bg-card/80 border-primary/30 ring-1 ring-primary/20" data-testid="team-auto-assign-notice">
            <CardContent className="p-5 text-center space-y-3">
              <Shield className="w-10 h-10 text-primary mx-auto" />
              <p className="font-heading text-lg uppercase">
                {fr ? 'Assignation en cours...' : 'Assignment in progress...'}
              </p>
              <p className="text-sm text-muted-foreground">
                {fr
                  ? 'Tu seras automatiquement assigne a une equipe pour equilibrer les forces. Verifie tes notifications!'
                  : 'You will be automatically assigned to a team to balance the forces. Check your notifications!'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Gamification V2 — Live panel: Weekly Boost + Perfect Day + Team Missions + Relative Leaderboard */}
        <GamificationPanel />

        {/* Team Bars */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="team-bars">
          <TeamBar
            team="rouge"
            teamData={data.rouge}
            teamInfo={data.teams.rouge}
            maxPoints={maxPoints}
            isUser={userTeam === 'rouge'}
            language={language}
          />
          <TeamBar
            team="bleu"
            teamData={data.bleu}
            teamInfo={data.teams.bleu}
            maxPoints={maxPoints}
            isUser={userTeam === 'bleu'}
            language={language}
          />
        </div>

        {/* MVP de la Semaine */}
        <WeeklyMVP />

        {/* User Score + Log Points */}
        {userTeam && (
          <Card className={`${TEAM_CONFIG[userTeam].bg} border ${TEAM_CONFIG[userTeam].border}`} data-testid="user-clash-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{data.teams[userTeam]?.emoji}</span>
                  <div>
                    <p className={`font-heading text-sm uppercase ${TEAM_CONFIG[userTeam].text}`}>
                      {fr ? 'Ta Contribution' : 'Your Contribution'}
                    </p>
                    <p className={`font-heading text-2xl font-black ${TEAM_CONFIG[userTeam].text}`} data-testid="user-clash-score">
                      {data.user_score} pts
                    </p>
                    {data.user_badge_glory_week > 0 && (
                      <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mt-0.5" data-testid="user-badge-glory-week">
                        {fr
                          ? `dont ${data.user_badge_glory_week} pts de Gloire · badges V2`
                          : `incl. ${data.user_badge_glory_week} Glory pts · V2 badges`}
                      </p>
                    )}
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${TEAM_CONFIG[userTeam].bg} ${TEAM_CONFIG[userTeam].border} border`}>
                  <Zap className="w-3.5 h-3.5" />
                  {fr ? 'Auto-sync actif' : 'Auto-sync active'}
                </div>
              </div>
              {/* Point guide */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px] text-muted-foreground">
                <div className="flex items-center gap-1"><UtensilsCrossed className="w-3 h-3" /> {fr ? 'Repas' : 'Meal'}: +{data.point_values?.meal_logged}</div>
                <div className="flex items-center gap-1"><Droplets className="w-3 h-3" /> {fr ? 'Eau' : 'Water'}: +{data.point_values?.hydration_goal}</div>
                <div className="flex items-center gap-1"><Dumbbell className="w-3 h-3" /> Workout: +{data.point_values?.workout_logged}</div>
                <div className="flex items-center gap-1"><Scale className="w-3 h-3" /> {fr ? 'Pesee' : 'Weigh-in'}: +{data.point_values?.weight_logged}</div>
              </div>
              <p className="text-[10px] text-amber-400/80 mt-2 flex items-center gap-1" data-testid="clash-badge-connection">
                <Sparkles className="w-3 h-3" />
                {fr
                  ? 'Les badges V2 debloques cette semaine ajoutent aussi de la Gloire a ta meute (10 a 400 pts).'
                  : 'V2 badges unlocked this week also add Glory to your pack (10 to 400 pts).'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* History */}
        <div className="text-center">
          <button
            onClick={loadHistory}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid="history-toggle"
          >
            <Trophy className="w-3 h-3" />
            {fr ? 'Historique des semaines' : 'Weekly history'}
            {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          <AnimatePresence>
            {showHistory && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden mt-3"
              >
                <div className="space-y-2 max-w-sm mx-auto">
                  {history.length === 0 && (
                    <p className="text-xs text-muted-foreground py-2">{fr ? 'Aucun historique' : 'No history yet'}</p>
                  )}
                  {history.map(h => (
                    <div key={h.week_start} className="flex items-center gap-3 p-2 rounded-lg bg-secondary/40 text-xs" data-testid={`history-${h.week_start}`}>
                      <span className="text-muted-foreground w-20">{h.week_start}</span>
                      <span className={`font-bold ${TEAM_CONFIG.rouge.text}`}>{h.rouge_points}</span>
                      <span className="text-muted-foreground">vs</span>
                      <span className={`font-bold ${TEAM_CONFIG.bleu.text}`}>{h.bleu_points}</span>
                      <span className="ml-auto">
                        {h.winner === 'tie' ? '🤝' : data.teams[h.winner]?.emoji}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
}
